import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { FunctionalityCode } from 'src/app/core/_enums/functionality.code';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { Icon } from 'src/app/core/_enums/icon';
import { LabelMenu } from 'src/app/core/_enums/label.menu';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Menu } from 'src/app/core/_models/menu';
import { Role } from 'src/app/core/_models/role';
import { AcheminementService } from 'src/app/core/_services/acheminement.service';
import { AgencyService } from 'src/app/core/_services/agency.service';
import { AuthService } from 'src/app/core/_services/auth.service';
import { CallForTenderService } from 'src/app/core/_services/call-for-tender.service';
import { CombustibleService } from 'src/app/core/_services/combustible.service';
import { EnergieService } from 'src/app/core/_services/energie.service';
import { EnginAtelierService } from 'src/app/core/_services/engin.atelier.service';
import { IndustrieService } from 'src/app/core/_services/industrie.service';
import { MemberService } from 'src/app/core/_services/member.service';
import { ProductFormulaService } from 'src/app/core/_services/product.formula.service';
import { ProjectService } from 'src/app/core/_services/project.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TransportService } from 'src/app/core/_services/transport.service';
import { UserService } from 'src/app/core/_services/user.service';
import { WorkInspectionService } from 'src/app/core/_services/workInspection.service';
import { ConfirmCancelDialogComponent } from '../shared/confirm-cancel-dialog/confirm-cancel-dialog.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';
import { RessourceListConfig } from '../shared/ressource-list/ressource-list.component';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';

export interface NavListCommonProps {
  perimeterId: string;
  dataSource: MatTreeNestedDataSource<any>;
  updateDataSource: (newDataSource: any[]) => void;
  navComponent?: NavComponent;
}

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss']
})
export class NavComponent implements OnInit, OnDestroy {
  subMenu!: string;
  user!: any;
  selectedRole!: Role;
  listItemMenu: Array<Menu> = [];
  SiteMapUrl = SiteMapUrl;
  isDeployed = false;
  hasSubMenu = false;

  ressourceListConfig: RessourceListConfig | null = null;
  dataSource: MatTreeNestedDataSource<any> = new MatTreeNestedDataSource<any>();
  configRefreshKey = 0;
  currentFilter?: GroupBy;

  private roleSubscription!: Subscription;
  private cftCreatedSubscription!: Subscription;
  private cftDeletedSubscription!: Subscription;
  private projectCreatedSubscription!: Subscription;
  private projectDeletedSubscription!: Subscription;
  private routerSubscription!: Subscription;
  private lastRootUrlSegment: string | null = null;

  constructor(
    public router: Router,
    private authService: AuthService,
    private spinnerService: SpinnerOverlayService,
    private acheminementService: AcheminementService,
    private projectService: ProjectService,
    private transportService: TransportService,
    private energieService: EnergieService,
    private enginAtelierService: EnginAtelierService,
    private agencyService: AgencyService,
    private combustibleService: CombustibleService,
    private industrieService: IndustrieService,
    private memberService: MemberService,
    private userService: UserService,
    private productFormulaService: ProductFormulaService,
    private dialog: MatDialog,
    private workInspectionService: WorkInspectionService,
    private callForTenderService: CallForTenderService,
    private translate: TranslateService,
    private resourceListRefreshService: ResourceListRefreshService
  ) {
    const user = this.authService.userValue;

    if (user && Object.keys(user).length !== 0) {
      this.user = user;

      const json = JSON.parse(localStorage.getItem('userRole') || '{}');
      this.selectedRole = user.roles.find((r: any) => r.profileCode == json.profileCode && r.euId == json.euId && r.perimeterId == json.perimeterId);

      if (this.selectedRole == null) {
        this.selectedRole = user.roles[0];
      }

      this.refreshMenuForRole(this.selectedRole);
    }
  }

  ngOnInit() {
    this.lastRootUrlSegment = this.resolveMenuFromUrl(this.router.url.split('/')[1]);
    this.loadCurrentRouteConfig();

    this.routerSubscription = this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
      const newRootSegment = this.resolveMenuFromUrl(this.router.url.split('/')[1]);
      if (newRootSegment === this.lastRootUrlSegment) {
        return;
      }
      this.lastRootUrlSegment = newRootSegment;
      this.loadCurrentRouteConfig();
    });

    this.roleSubscription = this.authService.roleSubject.subscribe((role) => {
      if (role && (role.perimeterId !== this.selectedRole?.perimeterId || role.profileCode !== this.selectedRole?.profileCode)) {
        this.refreshMenuForRole(role, true);
      }
    });

    this.cftCreatedSubscription = this.callForTenderService.cftCreated$.subscribe((cft) => {
      if (this.router.url.startsWith('/' + SiteMapUrl.MenuCallForTender)) {
        this.updateRessourceListData([cft, ...this.dataSource.data]);
      }
    });

    this.cftDeletedSubscription = this.callForTenderService.cftDeleted$.subscribe((deletedIds) => {
      const filteredData = this.dataSource.data.filter((item) => !deletedIds.includes(item.id));
      this.updateRessourceListData(filteredData);
    });

    this.projectDeletedSubscription = this.projectService.projectDeleted$.subscribe((deletedIds) => {
      const filteredData = this.dataSource.data.filter((item) => !deletedIds.includes(item.id));
      this.updateRessourceListData(filteredData);
    });

    this.projectCreatedSubscription = this.projectService.projectCreated$.subscribe((project) => {
      if (this.router.url.startsWith('/' + SiteMapUrl.MenuProject)) {
        this.updateRessourceListData([project, ...this.dataSource.data]);
      }
    });
    this.resourceListRefreshService.refresh$.subscribe(() => {
      this.refreshCurrentMenuData();
    });
  }

  private urlToMenuMap: { [key: string]: SiteMapUrl } = {
    [SiteMapUrl.MenuDredgingFuel]: SiteMapUrl.MenuEnergie
  };

  private resolveMenuFromUrl(urlSegment: string): string {
    return this.urlToMenuMap[urlSegment] ?? urlSegment;
  }

  private refreshCurrentMenuData() {
    if (!this.listItemMenu || this.listItemMenu.length === 0) {
      return;
    }

    const currentUrl = this.resolveMenuFromUrl(this.router.url.split('/')[1]);
    const currentMenuItem = this.listItemMenu.find((item) => item.siteMapUrl === currentUrl);

    if (!currentMenuItem?.ressourceListConfig) {
      return;
    }

    const filter = this.currentFilter ?? currentMenuItem.ressourceListConfig.header.defaultFilter;
    currentMenuItem.ressourceListConfig.getDataSource(filter).subscribe((data: any) => {
      this.applyRessourceListConfig(currentMenuItem, data, filter);
    });
  }

  ngOnDestroy() {
    this.roleSubscription?.unsubscribe();
    this.cftCreatedSubscription?.unsubscribe();
    this.cftDeletedSubscription?.unsubscribe();
    this.projectCreatedSubscription?.unsubscribe();
    this.projectDeletedSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
  }

  loadCurrentRouteConfig() {
    if (!this.listItemMenu || this.listItemMenu.length === 0) {
      return;
    }

    const currentUrl = this.resolveMenuFromUrl(this.router.url.split('/')[1]);
    const currentMenuItem = this.listItemMenu.find((item) => item.siteMapUrl === currentUrl);

    if (currentMenuItem) {
      this.loadMenuConfig(currentMenuItem);
    } else {
      this.ressourceListConfig = null;
      this.hasSubMenu = false;
    }
  }

  loadMenuConfig(item: Menu) {
    this.hasSubMenu = !!item.ressourceListConfig || item.siteMapUrl === SiteMapUrl.MenuUserEntity;
    if (!this.isDeployed) {
      this.isDeployed = true;
    }
    this.ressourceListConfig = null;
    this.dataSource.data = [];
    this.configRefreshKey++;
    this.currentFilter = undefined;
    if (!item.ressourceListConfig) {
      return;
    }
    this.fetchMenuData(item, item.ressourceListConfig.header.defaultFilter);
  }

  private refreshMenuForRole(newRole: Role, forceReload: boolean = false) {
    this.selectedRole = newRole;

    this.authService.getAccesses(newRole.profileCode).subscribe((list) => {
      this.listItemMenu = [];
      this.authService.accessesValues = list;
      localStorage.setItem('userRight', JSON.stringify(list));
      const didNavigate = this.showItemMenu(list);
      this.loadCurrentRouteConfig();

      if (forceReload && !didNavigate) {
        const currentUrl = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigateByUrl(currentUrl);
        });
      }
    });
  }

  public get perimeterId(): string {
    return this.selectedRole?.perimeterId;
  }

  showItemMenu(listAccesses: Array<string>): boolean {
    const commonProps: NavListCommonProps = {
      perimeterId: this.perimeterId,
      dataSource: this.dataSource,
      updateDataSource: (newDataSource: any[]) => this.updateRessourceListData(newDataSource),
      navComponent: this
    };

    const menuItems = [
      { access: [FunctionalityCode.PartnerApiProjects], menu: { siteMapUrl: SiteMapUrl.MenuPartnerApi, label: LabelMenu.PartnerApi, icon: Icon.Key, disabled: false } },
      {
        access: [FunctionalityCode.CallForTenderView],
        menu: { siteMapUrl: SiteMapUrl.MenuCallForTender, label: LabelMenu.CallForTender, icon: Icon.Dashboard, disabled: false, ressourceListConfig: this.callForTenderService.getNavConfig(commonProps) }
      },
      { access: [FunctionalityCode.ProjectView], menu: { siteMapUrl: SiteMapUrl.MenuProject, label: LabelMenu.Projects, icon: Icon.Highway, disabled: false, ressourceListConfig: this.projectService.getNavConfig(commonProps) } },
      {
        access: [FunctionalityCode.WorkInspectionView, FunctionalityCode.ManageWorkInspection],
        menu: { siteMapUrl: SiteMapUrl.MenuWorkInspection, label: LabelMenu.WorkInspection, icon: Icon.Checklist, disabled: false, ressourceListConfig: this.workInspectionService.getNavConfig(commonProps) }
      },
      { access: [FunctionalityCode.ProductView], menu: { siteMapUrl: SiteMapUrl.MenuProductFormula, label: LabelMenu.Products, icon: Icon.OilBarrel, disabled: false, ressourceListConfig: this.productFormulaService.getNavConfig(commonProps) } },
      { access: [FunctionalityCode.DeviceView], menu: { siteMapUrl: SiteMapUrl.MenuEnginAtelier, label: LabelMenu.Devices, icon: Icon.Excavator, disabled: false, ressourceListConfig: this.enginAtelierService.getNavConfig(commonProps) } },
      {
        access: [FunctionalityCode.TransportationView],
        menu: { siteMapUrl: SiteMapUrl.MenuAcheminement, label: LabelMenu.Transportations, icon: Icon.Route, disabled: false, ressourceListConfig: this.acheminementService.getNavConfig(commonProps) }
      },
      { access: [FunctionalityCode.TransportView], menu: { siteMapUrl: SiteMapUrl.MenuTransport, label: LabelMenu.Transport, icon: Icon.Truck, disabled: false, ressourceListConfig: this.transportService.getNavConfig(commonProps) } },
      { access: [FunctionalityCode.EnergyView], menu: { siteMapUrl: SiteMapUrl.MenuEnergie, label: LabelMenu.Energie, icon: Icon.Energy, disabled: false, ressourceListConfig: this.energieService.getNavConfig(commonProps) } },
      { access: [FunctionalityCode.IndustryView], menu: { siteMapUrl: SiteMapUrl.MenuIndustry, label: LabelMenu.Industries, icon: Icon.Industry, disabled: false, ressourceListConfig: this.industrieService.getNavConfig(commonProps) } },
      { access: [FunctionalityCode.CombustibleView], menu: { siteMapUrl: SiteMapUrl.MenuCombustible, label: LabelMenu.Combustible, icon: Icon.Gasoline, disabled: false, ressourceListConfig: this.combustibleService.getNavConfig(commonProps) } },
      { access: [FunctionalityCode.MemberView], menu: { siteMapUrl: SiteMapUrl.MenuMember, label: LabelMenu.Members, icon: Icon.Member, disabled: false, ressourceListConfig: this.memberService.getNavConfig(commonProps) } },
      { access: [FunctionalityCode.UEView], menu: { siteMapUrl: SiteMapUrl.MenuUserEntity, label: LabelMenu.UserEntities, icon: Icon.UserEntities, disabled: false } },
      { access: [FunctionalityCode.AgencyView], menu: { siteMapUrl: SiteMapUrl.MenuOrganisation, label: LabelMenu.Agencies, icon: Icon.Building, disabled: false, ressourceListConfig: this.agencyService.getNavConfig(commonProps) } },
      { access: [FunctionalityCode.UserView], menu: { siteMapUrl: SiteMapUrl.MenuUser, label: LabelMenu.Users, icon: Icon.User, disabled: false, ressourceListConfig: this.userService.getNavConfig(commonProps) } },
      { access: [FunctionalityCode.StatisticView, FunctionalityCode.ACDView], menu: { siteMapUrl: SiteMapUrl.MenuStatistic, label: LabelMenu.Statistics, icon: Icon.Statistic, disabled: false } },
      { access: [FunctionalityCode.ACEUView, FunctionalityCode.ABEView], menu: { siteMapUrl: SiteMapUrl.MenuStatisticProfile, label: LabelMenu.Statistics, icon: Icon.Statistic, disabled: false } },
      { access: [FunctionalityCode.AdministrationView], menu: { siteMapUrl: SiteMapUrl.MenuAdministration, label: LabelMenu.Administration, icon: Icon.Administration, disabled: false } },
      { access: [FunctionalityCode.ACUView], menu: { siteMapUrl: SiteMapUrl.MenuEmail, label: LabelMenu.Email, icon: Icon.Email, disabled: false } },
      { access: [], menu: { siteMapUrl: SiteMapUrl.MenuHelp, label: LabelMenu.Help, icon: Icon.Help, disabled: false } }
    ];

    menuItems.forEach((item) => {
      const hasAccess = item.access.length === 0 || item.access.some((access) => listAccesses.includes(access));
      if (hasAccess) {
        this.listItemMenu.push(item.menu);
      }
    });

    this.buildGroupedMenus();

    const hiddenRoutes = [SiteMapUrl.MenuTranslation];
    const urlMenu = this.resolveMenuFromUrl(this.router.url.split('/')[1]);

    if (hiddenRoutes.includes(urlMenu as SiteMapUrl)) {
      this.ressourceListConfig = null;
      this.isDeployed = false;
      return false;
    }

    const accessibleMenus = this.listItemMenu.map((itemMenu) => itemMenu.siteMapUrl);

    if (accessibleMenus.find((menu) => urlMenu == menu) != null) {
      return false;
    }

    if (this.listItemMenu && this.listItemMenu.length > 0) {
      this.router.navigate([this.listItemMenu[0].siteMapUrl]);
      const currentMenuItem = this.listItemMenu.find((item) => item.siteMapUrl === this.listItemMenu[0].siteMapUrl);

      if (currentMenuItem) {
        this.loadMenuConfig(currentMenuItem);
      } else {
        this.ressourceListConfig = null;
      }
      return true;
    }

    this.logout();
    return true;
  }

  deployNavbar() {
    this.isDeployed = !this.isDeployed;
  }

  async logout() {
    await this.authService.logout();
  }

  enabledMenus(menus: Menu[]) {
    return menus.filter((menu) => !menu.disabled);
  }

  private readonly menuGroups: Array<{ label: string; urls: string[] }> = [
    { label: 'nav_group_pilotage', urls: [SiteMapUrl.MenuPartnerApi, SiteMapUrl.MenuCallForTender, SiteMapUrl.MenuProject, SiteMapUrl.MenuWorkInspection] },
    {
      label: 'nav_group_database',
      urls: [SiteMapUrl.MenuProductFormula, SiteMapUrl.MenuEnginAtelier, SiteMapUrl.MenuAcheminement, SiteMapUrl.MenuTransport, SiteMapUrl.MenuEnergie, SiteMapUrl.MenuIndustry, SiteMapUrl.MenuCombustible]
    },
    {
      label: 'nav_group_management',
      urls: [SiteMapUrl.MenuMember, SiteMapUrl.MenuUserEntity, SiteMapUrl.MenuOrganisation, SiteMapUrl.MenuUser, SiteMapUrl.MenuStatistic, SiteMapUrl.MenuStatisticProfile, SiteMapUrl.MenuAdministration, SiteMapUrl.MenuEmail]
    },
    { label: 'nav_group_help', urls: [SiteMapUrl.MenuHelp] }
  ];

  groupedMenuView: Array<{ label: string; items: Menu[] }> = [];

  private buildGroupedMenus(): void {
    const enabled = this.enabledMenus(this.listItemMenu);
    const grouped = this.menuGroups.map((group) => ({ label: group.label, items: enabled.filter((menu) => group.urls.includes(menu.siteMapUrl)) }));
    const known = this.menuGroups.flatMap((group) => group.urls);
    const others = enabled.filter((menu) => !known.includes(menu.siteMapUrl));
    if (others.length) {
      grouped.push({ label: 'nav_group_other', items: others });
    }
    this.groupedMenuView = grouped.filter((group) => group.items.length);
  }

  trackByGroup(_index: number, group: { label: string }): string {
    return group.label;
  }

  trackByMenu(_index: number, menu: Menu): string {
    return menu.siteMapUrl;
  }

  private fetchMenuData(item: Menu, filter?: GroupBy) {
    if (!item.ressourceListConfig) {
      this.ressourceListConfig = null;
      return;
    }
    const targetFilter = filter ?? item.ressourceListConfig.header.defaultFilter;
    this.spinnerService.show();
    item.ressourceListConfig.getDataSource(targetFilter).subscribe((data: any) => {
      this.applyRessourceListConfig(item, data, targetFilter);
      this.spinnerService.hide();
    });
  }

  private applyRessourceListConfig(item: Menu, data: any[], filter?: GroupBy) {
    if (!item.ressourceListConfig) {
      return;
    }
    const baseConfig = item.ressourceListConfig;
    const effectiveFilter = filter ?? baseConfig.header.defaultFilter;
    this.currentFilter = effectiveFilter;
    this.dataSource.data = data;
    const deleteHandler = baseConfig.onDelete;
    const wrappedDelete = deleteHandler ? (selectedItems: any[]) => this.handleMassDelete(selectedItems, deleteHandler) : undefined;
    const shareHandler = baseConfig.onShare;
    const wrappedShare = shareHandler ? (selectedItems: any[]) => this.handleMassShare(selectedItems, shareHandler) : undefined;
    let actions = baseConfig.header.actions;
    if (baseConfig.header?.filterActionList) {
      actions = baseConfig.header.filterActionList(filter);
    }
    const header = {
      ...baseConfig.header,
      actions: actions,
      defaultFilter: effectiveFilter,
      onFilterChange: (selectedFilter: string) => {
        this.fetchMenuData(item, selectedFilter as GroupBy);
      }
    };
    this.ressourceListConfig = {
      ...baseConfig,
      dataSource: this.dataSource,
      header,
      onShare: wrappedShare,
      onDelete: wrappedDelete,
      onFamilyEdit: () => this.loadMenuConfig(item)
    } as RessourceListConfig;
    this.configRefreshKey++;
  }

  private handleMassDelete(selectedItems: any[], deleteHandler: (selectedItems: any[], groupBy?: GroupBy) => void) {
    if (!selectedItems?.length) {
      return;
    }

    const sharedProjects = selectedItems.filter((item) => item.isSharedToAgencies === true);

    if (sharedProjects.length > 0) {
      const sharedNames = sharedProjects.map((item) => item.displayName || item.code || this.translate.instant('nav_unnamed')).join(', ');
      this.dialog
        .open(ConfirmCancelDialogComponent, {
          width: '450px',
          data: {
            message: this.translate.instant('nav_confirm_delete_shared', { names: sharedNames })
          }
        })
        .afterClosed()
        .subscribe((confirmed) => {
          if (confirmed) {
            deleteHandler(selectedItems, this.currentFilter);
          }
        });
      return;
    }

    const title = selectedItems.length > 1 ? this.translate.instant('nav_confirm_delete_multiple', { count: selectedItems.length }) : this.translate.instant('nav_confirm_delete_single');
    this.dialog
      .open(DialogOverviewComponent, {
        data: { title }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          deleteHandler(selectedItems, this.currentFilter);
        }
      });
  }

  public createDeleteHandler<T extends { results?: Array<{ itemId?: string; resourceId?: string; id?: string; projectId?: string; success: boolean }> }>(config: {
    deleteMany: (ids: string[]) => import('rxjs').Observable<T>;
    getItemId: (item: any) => string | undefined;
    filterData: (data: any[], deletedIds: string[]) => any[];
    successMessage: (count: number) => string;
    errorMessage: string;
    snackbarService: any;
  }) {
    return (selectedItems: any[], dataSource: MatTreeNestedDataSource<any>, updateDataSource: (newDataSource: any[]) => void) => {
      if (!selectedItems.length) {
        config.snackbarService.open(SnackbarTypes.error, config.errorMessage);
        return;
      }

      const selectedIds = selectedItems.map((item) => config.getItemId(item)).filter((id): id is string => !!id);

      if (!selectedIds.length) {
        config.snackbarService.open(SnackbarTypes.error, config.errorMessage);
        return;
      }

      config.deleteMany(selectedIds).subscribe(
        (data) => {
          config.snackbarService.open(SnackbarTypes.delete, config.successMessage(selectedIds.length));

          const deletedIds = this.resolveDeletedIds(data?.results, selectedIds);
          const filteredData = config.filterData(dataSource.data, deletedIds);
          updateDataSource(filteredData);
        },
        (error) => {
          config.snackbarService.open(SnackbarTypes.error);
        }
      );
    };
  }

  private resolveDeletedIds(results: Array<{ itemId?: string; resourceId?: string; id?: string; projectId?: string; success: boolean }> | undefined, fallback: string[]): string[] {
    if (!results?.length) {
      return fallback;
    }

    const ids = results
      .filter((result) => result.success)
      .map((result) => result.itemId || result.resourceId || result.id || result.projectId)
      .filter((id): id is string => !!id);

    if (!ids.length) {
      return fallback;
    }

    return ids;
  }

  private handleMassShare(selectedItems: any[], shareHandler: (selectedItems: any[]) => void) {
    if (!selectedItems?.length) {
      return;
    }
    const title = selectedItems.length > 1 ? this.translate.instant('nav_confirm_share_multiple', { count: selectedItems.length }) : this.translate.instant('nav_confirm_share_single');
    this.dialog
      .open(DialogOverviewComponent, {
        data: { title }
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          shareHandler(selectedItems);
        }
      });
  }

  private updateRessourceListData(newData: any[]) {
    this.dataSource.data = newData;
    if (!this.ressourceListConfig) {
      return;
    }
    const header = {
      ...this.ressourceListConfig.header,
      defaultFilter: this.currentFilter ?? this.ressourceListConfig.header.defaultFilter
    };
    this.ressourceListConfig = {
      ...this.ressourceListConfig,
      dataSource: this.dataSource,
      header
    };
    this.configRefreshKey++;
  }
}
